import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter } from 'recharts';

const StopeGeology = ({ stopeID }) => {
  const BASE_URL = 'https://pickstonepresentation.dumisaminerals.com/server/api';
  const [geologyData, setGeologyData] = useState(null);
  const [benchSamplingData, setBenchSamplingData] = useState([]);
  const [developmentSamplingData, setDevelopmentSamplingData] = useState([]);
  const [trammingSamplingData, setTrammingSamplingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [benchSamplingLoading, setBenchSamplingLoading] = useState(false);
  const [developmentSamplingLoading, setDevelopmentSamplingLoading] = useState(false);
  const [trammingSamplingLoading, setTrammingSamplingLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [devCurrentPage, setDevCurrentPage] = useState(1);
  const [devItemsPerPage] = useState(10);
  const [trammingCurrentPage, setTrammingCurrentPage] = useState(1);
  const [trammingItemsPerPage] = useState(10);
  const [xAxis, setXAxis] = useState('DateReported');
  const [yAxis, setYAxis] = useState('Grade');
  const [chartType, setChartType] = useState('bar');
  const [devXAxis, setDevXAxis] = useState('DateReported');
  const [devYAxis, setDevYAxis] = useState('Au');
  const [devChartType, setDevChartType] = useState('bar');
  const [trammingXAxis, setTrammingXAxis] = useState('DateReported');
  const [trammingYAxis, setTrammingYAxis] = useState('Grade');
  const [trammingChartType, setTrammingChartType] = useState('bar');

  // Pagination logic
  const totalPages = Math.ceil(benchSamplingData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = benchSamplingData.slice(startIndex, endIndex);

  // DevelopmentSampling pagination logic
  const devTotalPages = Math.ceil(developmentSamplingData.length / devItemsPerPage);
  const devStartIndex = (devCurrentPage - 1) * devItemsPerPage;
  const devEndIndex = devStartIndex + devItemsPerPage;
  const devCurrentData = developmentSamplingData.slice(devStartIndex, devEndIndex);

  // TrammingSampling pagination logic
  const trammingTotalPages = Math.ceil(trammingSamplingData.length / trammingItemsPerPage);
  const trammingStartIndex = (trammingCurrentPage - 1) * trammingItemsPerPage;
  const trammingEndIndex = trammingStartIndex + trammingItemsPerPage;
  const trammingCurrentData = trammingSamplingData.slice(trammingStartIndex, trammingEndIndex);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [benchSamplingData]);

  useEffect(() => {
    setDevCurrentPage(1);
  }, [developmentSamplingData]);

  useEffect(() => {
    setTrammingCurrentPage(1);
  }, [trammingSamplingData]);

  // Get unique values for dropdowns
  const getUniqueValues = (field) => {
    const values = [...new Set(benchSamplingData.map(item => item[field]).filter(val => val !== null && val !== undefined))];
    return values.sort();
  };

  // Get numeric columns for Y axis (excluding From, To, Distance, Depth)
  const getNumericColumns = () => {
    const numericFields = ['Grade', 'SampleWidth']; // Grade first as it's most commonly used
    return numericFields.filter(field => benchSamplingData.some(item => !isNaN(parseFloat(item[field]))));
  };

  // Get all columns for X axis (excluding StopeID, Level, Section, Distance, Depth, From, To, SampleWidth, Grade)
  const getAllColumns = () => {
    if (benchSamplingData.length === 0) return [];
    const excludedFields = ['StopeID', 'Level', 'Section', 'Distance', 'Depth', 'From', 'To', 'SampleWidth', 'Grade'];
    const availableFields = Object.keys(benchSamplingData[0]).filter(field => !excludedFields.includes(field));
    
    // Define preferred order for better UX
    const preferredOrder = [
      'DateReported',
      'Month',
      'Location', 
      'SampleType',
      'Letter',
      'Direction',
      'PegID',
      'SampleID',
      'Sampler',
      'Geotech'
    ];
    
    // Sort fields according to preferred order, then add any remaining fields
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Always add Month as it's a computed field
    if (!orderedFields.includes('Month')) {
      orderedFields.splice(1, 0, 'Month'); // Insert after DateReported
    }
    
    // Add any remaining fields not in preferred order
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Get all columns for DevelopmentSampling X axis (excluding StopeID, Level, Section, Distance, From, To, SampleWidth, Au)
  const getDevAllColumns = () => {
    if (developmentSamplingData.length === 0) return [];
    const excludedFields = ['StopeID', 'Level', 'Section', 'Distance', 'From', 'To', 'SampleWidth', 'Au'];
    const availableFields = Object.keys(developmentSamplingData[0]).filter(field => !excludedFields.includes(field));
    
    // Define preferred order for better UX
    const preferredOrder = [
      'DateReported',
      'Month',
      'HoleID',
      'Location', 
      'SampleType',
      'Direction',
      'PegID',
      'STD',
      'SampleID',
      'Sampler',
      'Geotech',
      'RCH NUMBER'
    ];
    
    // Sort fields according to preferred order, then add any remaining fields
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Always add Month as it's a computed field
    if (!orderedFields.includes('Month')) {
      orderedFields.splice(1, 0, 'Month'); // Insert after DateReported
    }
    
    // Add any remaining fields not in preferred order
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Get all columns for TrammingSampling X axis (excluding StopeID, Level, Section, Grade)
  const getTrammingAllColumns = () => {
    if (trammingSamplingData.length === 0) return [];
    const excludedFields = ['StopeID', 'Level', 'Section', 'Grade'];
    const availableFields = Object.keys(trammingSamplingData[0]).filter(field => !excludedFields.includes(field));
    
    // Define preferred order for better UX
    const preferredOrder = [
      'DateReported',
      'Month',
      'OG_SampleID',
      'Letter',
      'Source',
      'SourceID',
      'SampleID'
    ];
    
    // Sort fields according to preferred order, then add any remaining fields
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Always add Month as it's a computed field
    if (!orderedFields.includes('Month')) {
      orderedFields.splice(1, 0, 'Month'); // Insert after DateReported
    }
    
    // Add any remaining fields not in preferred order
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Format date for better readability
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr; // Return original if invalid
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateStr; // Return original if parsing fails
    }
  };

  // Process data for chart (handle averaging for grade)
  const processChartData = () => {
    if (!benchSamplingData.length) return [];

    const grouped = {};
    const originalDates = {}; // Store original dates for sorting
    
    benchSamplingData.forEach(item => {
      let key = item[xAxis];
      let originalDate = null;
      
      // Format date if X-axis is DateReported
      if (xAxis === 'DateReported') {
        originalDate = item[xAxis]; // Store original date
        key = formatDate(key);
      } else if (xAxis === 'Month') {
        // Create Month field from DateReported
        const date = new Date(item.DateReported);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [xAxis]: key,
          count: 0,
          totalGrade: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      // Add all numeric fields
      getNumericColumns().forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    // Calculate averages and return processed data
    const processedData = Object.values(grouped).map(group => {
      const processed = { [xAxis]: group[xAxis], count: group.count };
      
      getNumericColumns().forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Special handling for SampleWidth - use sum instead of average
          if (field === 'SampleWidth') {
            processed[field] = total; // Use sum for SampleWidth
          } else {
            const average = total / group.count;
            // Format Grade to 2 decimal places
            if (field === 'Grade') {
              processed[field] = parseFloat(average.toFixed(2));
            } else {
              processed[field] = average;
            }
          }
          processed[`${field}_total`] = total; // Sum
        }
      });
      
      return processed;
    });

    // Sort by date if X-axis is DateReported
    if (xAxis === 'DateReported') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[xAxis]]);
        const dateB = new Date(originalDates[b[xAxis]]);
        return dateA - dateB; // Ascending order
      });
    }

    if (xAxis === 'Month') {
      return processedData.sort((a, b) => {
        // Sort by year first, then by month
        const [monthA, yearA] = a[xAxis].split('_');
        const [monthB, yearB] = b[xAxis].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const chartData = processChartData();

  // DevelopmentSampling chart data processing
  const processDevelopmentChartData = () => {
    if (!developmentSamplingData.length) return [];

    const grouped = {};
    const originalDates = {}; // Store original dates for sorting
    
    developmentSamplingData.forEach(item => {
      let key = item[devXAxis];
      let originalDate = null;
      
      // Format date if X-axis is DateReported
      if (devXAxis === 'DateReported') {
        originalDate = item[devXAxis]; // Store original date
        key = formatDate(key);
      } else if (devXAxis === 'Month') {
        // Create Month field from DateReported
        const date = new Date(item.DateReported);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [devXAxis]: key,
          count: 0,
          totalAu: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      // Add all numeric fields
      const numericFields = ['Au', 'SampleWidth', 'Distance', 'From', 'To'];
      numericFields.forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    // Calculate averages and return processed data
    const processedData = Object.values(grouped).map(group => {
      const processed = { [devXAxis]: group[devXAxis], count: group.count };
      
      const numericFields = ['Au', 'SampleWidth', 'Distance', 'From', 'To'];
      numericFields.forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Special handling for SampleWidth - use sum instead of average
          if (field === 'SampleWidth') {
            processed[field] = total; // Use sum for SampleWidth
          } else {
            const average = total / group.count;
            // Format Au to 2 decimal places
            if (field === 'Au') {
              processed[field] = parseFloat(average.toFixed(2));
            } else {
              processed[field] = average;
            }
          }
          processed[`${field}_total`] = total; // Sum
        }
      });
      
      return processed;
    });

    // Sort by date if X-axis is DateReported
    if (devXAxis === 'DateReported') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[devXAxis]]);
        const dateB = new Date(originalDates[b[devXAxis]]);
        return dateA - dateB; // Ascending order
      });
    }

    if (devXAxis === 'Month') {
      return processedData.sort((a, b) => {
        // Sort by year first, then by month
        const [monthA, yearA] = a[devXAxis].split('_');
        const [monthB, yearB] = b[devXAxis].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const devChartData = processDevelopmentChartData();

  // TrammingSampling chart data processing
  const processTrammingChartData = () => {
    if (!trammingSamplingData.length) return [];

    const grouped = {};
    const originalDates = {}; // Store original dates for sorting
    
    trammingSamplingData.forEach(item => {
      let key = item[trammingXAxis];
      let originalDate = null;
      
      // Format date if X-axis is DateReported
      if (trammingXAxis === 'DateReported') {
        originalDate = item[trammingXAxis]; // Store original date
        key = formatDate(key);
      } else if (trammingXAxis === 'Month') {
        // Create Month field from DateReported
        const date = new Date(item.DateReported);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          key = `${month}_${year}`;
        } else {
          key = 'Invalid Date';
        }
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [trammingXAxis]: key,
          count: 0,
          totalGrade: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      // Add Grade field
      const gradeValue = parseFloat(item.Grade);
      if (!isNaN(gradeValue)) {
        grouped[key].totalGrade += gradeValue;
      }
    });

    // Calculate averages and return processed data
    const processedData = Object.values(grouped).map(group => {
      const processed = { [trammingXAxis]: group[trammingXAxis], count: group.count };
      
      // Calculate average Grade
      if (group.totalGrade !== undefined) {
        const averageGrade = group.totalGrade / group.count;
        processed.Grade = parseFloat(averageGrade.toFixed(2));
        processed.Grade_total = group.totalGrade; // Sum
      }
      
      return processed;
    });

    // Sort by date if X-axis is DateReported
    if (trammingXAxis === 'DateReported') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[trammingXAxis]]);
        const dateB = new Date(originalDates[b[trammingXAxis]]);
        return dateA - dateB; // Ascending order
      });
    }

    if (trammingXAxis === 'Month') {
      return processedData.sort((a, b) => {
        // Sort by year first, then by month
        const [monthA, yearA] = a[trammingXAxis].split('_');
        const [monthB, yearB] = b[trammingXAxis].split('_');
        const yearCompare = parseInt(yearA) - parseInt(yearB);
        if (yearCompare !== 0) return yearCompare;
        return parseInt(monthA) - parseInt(monthB);
      });
    }

    return processedData;
  };

  const trammingChartData = processTrammingChartData();

  // Filter bench sampling data by stopeID
  const filterBenchSamplingByStope = (data) => {
    if (!stopeID || !data.length) return data;
    
    return data.filter(item => {
      if (!item.StopeID) return false;
      
      // Database StopeID (shorter) should be the needle
      // Parameter stopeID (longer) is the haystack
      const databaseStopeID = item.StopeID; // e.g., "5L_W2"
      const parameterStopeID = stopeID; // e.g., "5L_W2B_STP"
      
      // Check if database StopeID is at least 5 characters long and contains "L_"
      if (databaseStopeID.length < 5 || !databaseStopeID.includes('L_')) {
        return false;
      }
      
      // Check if parameter stopeID starts with database StopeID
      if (parameterStopeID.startsWith(databaseStopeID)) {
        // Get the character right after the database StopeID in the parameter
        const nextChar = parameterStopeID.charAt(databaseStopeID.length);
        
        // If there's no next character (exact match) or next character is not a number, include it
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  // Filter development sampling data by stopeID
  const filterDevelopmentSamplingByStope = (data) => {
    if (!stopeID || !data.length) return data;
    
    return data.filter(item => {
      if (!item.StopeID) return false;
      
      // Database StopeID (shorter) should be the needle
      // Parameter stopeID (longer) is the haystack
      const databaseStopeID = item.StopeID; // e.g., "5L_W2"
      const parameterStopeID = stopeID; // e.g., "5L_W2B_STP"
      
      // Check if database StopeID is at least 5 characters long and contains "L_"
      if (databaseStopeID.length < 5 || !databaseStopeID.includes('L_')) {
        return false;
      }
      
      // Check if parameter stopeID starts with database StopeID
      if (parameterStopeID.startsWith(databaseStopeID)) {
        // Get the character right after the database StopeID in the parameter
        const nextChar = parameterStopeID.charAt(databaseStopeID.length);
        
        // If there's no next character (exact match) or next character is not a number, include it
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  // Filter tramming sampling data by stopeID
  const filterTrammingSamplingByStope = (data) => {
    if (!stopeID || !data.length) return data;
    
    return data.filter(item => {
      if (!item.StopeID) return false;
      
      // Database StopeID (shorter) should be the needle
      // Parameter stopeID (longer) is the haystack
      const databaseStopeID = item.StopeID; // e.g., "5L_W2"
      const parameterStopeID = stopeID; // e.g., "5L_W2B_STP"
      
      // Check if database StopeID is at least 5 characters long and contains "L_"
      if (databaseStopeID.length < 5 || !databaseStopeID.includes('L_')) {
        return false;
      }
      
      // Check if parameter stopeID starts with database StopeID
      if (parameterStopeID.startsWith(databaseStopeID)) {
        // Get the character right after the database StopeID in the parameter
        const nextChar = parameterStopeID.charAt(databaseStopeID.length);
        
        // If there's no next character (exact match) or next character is not a number, include it
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  useEffect(() => {
    if (stopeID) {
      setLoading(true);
      setBenchSamplingLoading(true);
      setDevelopmentSamplingLoading(true);
      setTrammingSamplingLoading(true);

      // Fetch bench sampling data
      const fetchBenchSamplingData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/benchsampling`);
          if (!response.ok) throw new Error('Failed to fetch bench sampling data');
          
          const result = await response.json();
          const filteredData = filterBenchSamplingByStope(result);
          
          console.log('Bench sampling - Original data count:', result.length);
          console.log('Bench sampling - Filtered data count:', filteredData.length);
          console.log('Bench sampling - Parameter stopeID (haystack):', stopeID);
          console.log('Bench sampling - Sample database StopeIDs:', result.slice(0, 5).map(item => item.StopeID));
          
          setBenchSamplingData(filteredData);
          
          // Generate summary data from actual bench sampling data
          if (filteredData.length > 0) {
            const grades = filteredData.map(item => parseFloat(item.Grade)).filter(grade => !isNaN(grade));
            const dates = filteredData.map(item => new Date(item.DateReported)).filter(date => !isNaN(date.getTime()));
            
            const summaryData = {
              sampleCount: filteredData.length,
              averageGrade: grades.length > 0 ? (grades.reduce((sum, grade) => sum + grade, 0) / grades.length).toFixed(2) : 'N/A',
              minDate: dates.length > 0 ? formatDate(new Date(Math.min(...dates))) : 'N/A',
              maxDate: dates.length > 0 ? formatDate(new Date(Math.max(...dates))) : 'N/A',
              gradeRange: grades.length > 0 ? `${Math.min(...grades).toFixed(2)} - ${Math.max(...grades).toFixed(2)}` : 'N/A'
            };
            
            setGeologyData(summaryData);
          } else {
            setGeologyData({
              sampleCount: 0,
              averageGrade: 'N/A',
              minDate: 'N/A',
              maxDate: 'N/A',
              gradeRange: 'N/A'
            });
          }
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching bench sampling data:', err);
          setBenchSamplingData([]);
          setGeologyData({
            sampleCount: 0,
            averageGrade: 'N/A',
            minDate: 'N/A',
            maxDate: 'N/A',
            gradeRange: 'N/A'
          });
          setLoading(false);
        } finally {
          setBenchSamplingLoading(false);
        }
      };

      // Fetch development sampling data
      const fetchDevelopmentSamplingData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/developmentsampling`);
          if (!response.ok) throw new Error('Failed to fetch development sampling data');
          
          const result = await response.json();
          const filteredData = filterDevelopmentSamplingByStope(result);
          
          console.log('Development sampling - Original data count:', result.length);
          console.log('Development sampling - Filtered data count:', filteredData.length);
          
          setDevelopmentSamplingData(filteredData);
        } catch (err) {
          console.error('Error fetching development sampling data:', err);
          setDevelopmentSamplingData([]);
        } finally {
          setDevelopmentSamplingLoading(false);
        }
      };

      // Fetch tramming sampling data
      const fetchTrammingSamplingData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/trammingsampling`);
          if (!response.ok) throw new Error('Failed to fetch tramming sampling data');
          
          const result = await response.json();
          const filteredData = filterTrammingSamplingByStope(result);
          
          console.log('Tramming sampling - Original data count:', result.length);
          console.log('Tramming sampling - Filtered data count:', filteredData.length);
          
          setTrammingSamplingData(filteredData);
        } catch (err) {
          console.error('Error fetching tramming sampling data:', err);
          setTrammingSamplingData([]);
        } finally {
          setTrammingSamplingLoading(false);
        }
      };

      fetchBenchSamplingData();
      fetchDevelopmentSamplingData();
      fetchTrammingSamplingData();
    }
  }, [stopeID]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-2 text-gray-600">Loading geology data...</span>
      </div>
    );
  }


  // Calculate summary statistics for each sampling type
  const calculateSummaryStats = (data, gradeField = 'Grade') => {
    if (!data || data.length === 0) {
      return {
        sampleCount: 0,
        averageGrade: 'N/A',
        minDate: 'N/A',
        maxDate: 'N/A',
        gradeRange: 'N/A'
      };
    }

    const grades = data.map(item => parseFloat(item[gradeField])).filter(grade => !isNaN(grade));
    const dates = data.map(item => new Date(item.DateReported)).filter(date => !isNaN(date.getTime()));
    
    return {
      sampleCount: data.length,
      averageGrade: grades.length > 0 ? (grades.reduce((sum, grade) => sum + grade, 0) / grades.length).toFixed(2) : 'N/A',
      minDate: dates.length > 0 ? formatDate(new Date(Math.min(...dates))) : 'N/A',
      maxDate: dates.length > 0 ? formatDate(new Date(Math.max(...dates))) : 'N/A',
      gradeRange: grades.length > 0 ? `${Math.min(...grades).toFixed(2)} - ${Math.max(...grades).toFixed(2)}` : 'N/A'
    };
  };

  const benchStats = calculateSummaryStats(benchSamplingData, 'Grade');
  const devStats = calculateSummaryStats(developmentSamplingData, 'Au');
  const trammingStats = calculateSummaryStats(trammingSamplingData, 'Grade');

  return (
    <div className="space-y-4">
      {/* Summary Statistics Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 p-3 border-b border-gray-200">Sampling Summary</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Metric</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Bench</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Development</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Tramming</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Sample Count</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{benchStats.sampleCount}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{devStats.sampleCount}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{trammingStats.sampleCount}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Average Grade</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{benchStats.averageGrade}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{devStats.averageGrade}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{trammingStats.averageGrade}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Date Range</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{benchStats.minDate} to {benchStats.maxDate}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{devStats.minDate} to {devStats.maxDate}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{trammingStats.minDate} to {trammingStats.maxDate}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900 border-r border-gray-200">Grade Range</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{benchStats.gradeRange}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center border-r border-gray-200">{devStats.gradeRange}</td>
                <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 text-center">{trammingStats.gradeRange}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bench Sampling Table */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Bench Sampling Data</h3>
        
        {benchSamplingLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-2 text-gray-600">Loading bench sampling data...</span>
          </div>
        ) : benchSamplingData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Date</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">SampleType</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Distance</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Grade</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sampler</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                 {currentData.map((item, index) => {
                   // Format date for better readability
                   const formatDate = (dateStr) => {
                     if (!dateStr) return 'N/A';
                     try {
                       const date = new Date(dateStr);
                       if (isNaN(date.getTime())) return dateStr; // Return original if invalid
                       return date.toLocaleDateString('en-US', {
                         year: 'numeric',
                         month: 'short',
                         day: 'numeric'
                       });
                     } catch (error) {
                       return dateStr; // Return original if parsing fails
                     }
                   };

                   return (
                     <tr key={index} className="hover:bg-gray-50">
                       <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{formatDate(item.DateReported)}</td>
                       <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.SampleType}</td>
                       <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.Distance}</td>
                       <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.Grade ? parseFloat(item.Grade).toFixed(2) : 'N/A'}</td>
                       <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">{item.Sampler}</td>
                     </tr>
                   );
                 })}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {benchSamplingData.length > itemsPerPage && (
              <div className="flex items-center justify-center space-x-2 p-2 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="First page"
                >
                  |&lt;
                </button>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Previous page"
                >
                  &lt;
                </button>
                
                <span className="text-xs text-gray-600 px-2">
                  {currentPage} / {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Next page"
                >
                  &gt;
                </button>
                
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Last page"
                >
                  &gt;|
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500">
            <p>No bench sampling data available for this stope.</p>
          </div>
        )}
      </div>

      {/* Interactive Graph Section */}
      {benchSamplingData.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Bench Sampling Graph</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">X Axis</label>
                <select 
                  value={xAxis} 
                  onChange={(e) => setXAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {getAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Y Axis</label>
                <select 
                  value={yAxis} 
                  onChange={(e) => setYAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="count">Count</option>
                  {getNumericColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                <select 
                  value={chartType} 
                  onChange={(e) => setChartType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button 
                  onClick={() => {
                    setXAxis('DateReported');
                    setYAxis('Grade');
                    setChartType('bar');
                  }}
                  className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Chart */}
            <div className="w-full" style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bar' && (
                  <BarChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={yAxis} fill="#f59e0b" />
                  </BarChart>
                )}
                
                {chartType === 'line' && (
                  <LineChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={yAxis} stroke="#f59e0b" strokeWidth={2} />
                  </LineChart>
                )}
                
                {chartType === 'scatter' && (
                  <ScatterChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Scatter dataKey={yAxis} fill="#f59e0b" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Development Sampling Table */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Development Sampling Data</h3>
        
        {developmentSamplingLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-2 text-gray-600">Loading development sampling data...</span>
          </div>
        ) : developmentSamplingData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Date</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">HoleID</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">SampleType</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Distance</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Au</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sampler</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {devCurrentData.map((item, index) => {
                  // Format date for better readability
                  const formatDate = (dateStr) => {
                    if (!dateStr) return 'N/A';
                    try {
                      const date = new Date(dateStr);
                      if (isNaN(date.getTime())) return dateStr; // Return original if invalid
                      return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    } catch (error) {
                      return dateStr; // Return original if parsing fails
                    }
                  };

                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{formatDate(item.DateReported)}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.HoleID}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.SampleType}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.Distance}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.Au ? parseFloat(item.Au).toFixed(2) : 'N/A'}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">{item.Sampler}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* DevelopmentSampling Pagination Controls */}
            {developmentSamplingData.length > devItemsPerPage && (
              <div className="flex items-center justify-center space-x-2 p-2 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setDevCurrentPage(1)}
                  disabled={devCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="First page"
                >
                  |&lt;
                </button>
                
                <button
                  onClick={() => setDevCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={devCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Previous page"
                >
                  &lt;
                </button>
                
                <span className="text-xs text-gray-600 px-2">
                  {devCurrentPage} / {devTotalPages}
                </span>
                
                <button
                  onClick={() => setDevCurrentPage(prev => Math.min(prev + 1, devTotalPages))}
                  disabled={devCurrentPage === devTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Next page"
                >
                  &gt;
                </button>
                
                <button
                  onClick={() => setDevCurrentPage(devTotalPages)}
                  disabled={devCurrentPage === devTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Last page"
                >
                  &gt;|
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500">
            <p>No development sampling data available for this stope.</p>
          </div>
        )}
      </div>

      {/* Development Sampling Interactive Graph Section */}
      {developmentSamplingData.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Development Sampling Graph</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">X Axis</label>
                <select 
                  value={devXAxis} 
                  onChange={(e) => setDevXAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {getDevAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Y Axis</label>
                <select 
                  value={devYAxis} 
                  onChange={(e) => setDevYAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="count">Count</option>
                  <option value="Au">Au</option>
                  <option value="SampleWidth">SampleWidth</option>
                  <option value="Distance">Distance</option>
                  <option value="From">From</option>
                  <option value="To">To</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                <select 
                  value={devChartType} 
                  onChange={(e) => setDevChartType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button 
                  onClick={() => {
                    setDevXAxis('DateReported');
                    setDevYAxis('Au');
                    setDevChartType('bar');
                  }}
                  className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Chart */}
            <div className="w-full" style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {devChartType === 'bar' && (
                  <BarChart 
                    data={devChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={devXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={devYAxis} fill="#f59e0b" />
                  </BarChart>
                )}
                
                {devChartType === 'line' && (
                  <LineChart 
                    data={devChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={devXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={devYAxis} stroke="#f59e0b" strokeWidth={2} />
                  </LineChart>
                )}
                
                {devChartType === 'scatter' && (
                  <ScatterChart 
                    data={devChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={devXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Scatter dataKey={devYAxis} fill="#f59e0b" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Tramming Sampling Data Section */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Tramming Sampling Data</h3>
        
        {trammingSamplingLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-2 text-gray-600">Loading tramming sampling data...</span>
          </div>
        ) : trammingSamplingData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Date Reported</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">OG Sample ID</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Source</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Source ID</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Sample ID</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                </tr>
              </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trammingCurrentData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">
                        {formatDate(item.DateReported)}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.OG_SampleID || 'N/A'}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.Source || 'N/A'}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.SourceID || 'N/A'}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 border-r border-gray-200">{item.SampleID || 'N/A'}</td>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900">
                        {item.Grade ? parseFloat(item.Grade).toFixed(2) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* TrammingSampling Pagination Controls */}
            {trammingSamplingData.length > trammingItemsPerPage && (
              <div className="flex items-center justify-center space-x-2 p-2 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setTrammingCurrentPage(1)}
                  disabled={trammingCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="First page"
                >
                  |&lt;
                </button>
                
                <button
                  onClick={() => setTrammingCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={trammingCurrentPage === 1}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Previous page"
                >
                  &lt;
                </button>
                
                <span className="text-xs text-gray-600 px-2">
                  {trammingCurrentPage} / {trammingTotalPages}
                </span>
                
                <button
                  onClick={() => setTrammingCurrentPage(prev => Math.min(prev + 1, trammingTotalPages))}
                  disabled={trammingCurrentPage === trammingTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Next page"
                >
                  &gt;
                </button>
                
                <button
                  onClick={() => setTrammingCurrentPage(trammingTotalPages)}
                  disabled={trammingCurrentPage === trammingTotalPages}
                  className="w-6 h-6 flex items-center justify-center text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 rounded"
                  title="Last page"
                >
                  &gt;|
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500">
            <p>No tramming sampling data available for this stope.</p>
          </div>
        )}
      </div>

      {/* Tramming Sampling Interactive Graph Section */}
      {trammingSamplingData.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Tramming Sampling Graph</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">X Axis</label>
                <select 
                  value={trammingXAxis} 
                  onChange={(e) => setTrammingXAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {getTrammingAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Y Axis</label>
                <select 
                  value={trammingYAxis} 
                  onChange={(e) => setTrammingYAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="Grade">Grade</option>
                  <option value="count">Count</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                <select 
                  value={trammingChartType} 
                  onChange={(e) => setTrammingChartType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setTrammingXAxis('DateReported');
                    setTrammingYAxis('Grade');
                    setTrammingChartType('bar');
                  }}
                  className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Chart Container */}
            <div style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {trammingChartType === 'bar' && (
                  <BarChart 
                    data={trammingChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={trammingXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={trammingYAxis} fill="#10b981" />
                  </BarChart>
                )}
                
                {trammingChartType === 'line' && (
                  <LineChart 
                    data={trammingChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={trammingXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={trammingYAxis} stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                )}
                
                {trammingChartType === 'scatter' && (
                  <ScatterChart 
                    data={trammingChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 0,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={trammingXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Scatter dataKey={trammingYAxis} fill="#10b981" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StopeGeology;
